import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Label, NumberInput, Button } from "@tellescope/ui";
import type { EnduserObservation } from "@tellescope/types-models";
import { mlToOz, ozToMl } from "../../../utils/nutritionUnitConversions";

interface WaterEntryFieldProps {
  /** When set, the form pre-fills from this entry and submits via onUpdate instead of onLog. */
  editingEntry?: EnduserObservation;
  onLog: (valueInMl: number) => Promise<void>;
  onUpdate?: (valueInMl: number) => Promise<void>;
  isPending: boolean;
}

export const WaterEntryField = ({ editingEntry, onLog, onUpdate, isPending }: WaterEntryFieldProps) => {
  const { t } = useTranslation("portal");
  const [value, setValue] = useState<number | undefined>(
    editingEntry ? mlToOz(editingEntry.measurement.value) : undefined
  );

  const handleSubmit = async () => {
    if (value === undefined || value <= 0) return;
    const valueInMl = ozToMl(value);
    if (editingEntry) {
      await onUpdate?.(valueInMl);
    } else {
      await onLog(valueInMl);
      setValue(undefined);
    }
  };

  return (
    <div className="space-y-3">
      {!editingEntry && <p className="text-sm text-muted-foreground">{t("Add water intake")}</p>}
      <div className="space-y-1.5">
        <Label>{t("Amount")}</Label>
        <NumberInput
          value={value}
          onChange={setValue}
          precisionOptions={{ maxDecimalDigitCount: 1 }}
          placeholder="0.0"
          data-test="water-input"
        />
        <p className="text-xs text-muted-foreground">{t("oz")}</p>
      </div>
      <Button
        onClick={handleSubmit}
        disabled={value === undefined || value <= 0 || isPending}
        data-test="log-water-button"
      >
        {editingEntry ? t("Update reading") : t("Save reading")}
      </Button>
    </div>
  );
};
